/**
 * Copyright (c) 2024 Epinics (PVT) LTD
 * All Rights Reserved.
 * <p>
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * <p>
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * <p>
 */
import Axios from "../../../common/util/Axios";

import {
  SAMPLE_TYPE,
  SET_STEP_ONE_DETAILS,
  SET_STEP_TWO_DETAILS,
  SUBMIT_SUCCESSFUL,
  SUBMIT_FAILED,
  SET_IS_LOADING_TRUE,
  SET_IS_LOADING_FALSE,
  CLEAR_ALL_DETAILS,
} from "./types";

import { showSnackbar } from "../../../common/actions";

export const sampleAction = state => async dispatch => {
  try {
    dispatch({
      type: SAMPLE_TYPE,
      payload: state,
    });
  } catch (err) {
    console.log(err);
  }
};

export const setStepOneDetails = state => async dispatch => {
  try {
    dispatch({
      type: SET_STEP_ONE_DETAILS,
      payload: state,
    });
  } catch (err) {
    console.log(err);
  }
};

export const setStepTwoDetails = state => async dispatch => {
  try {
    dispatch({
      type: SET_STEP_TWO_DETAILS,
      payload: state,
    });
  } catch (err) {
    console.log(err);
  }
};

export const submitDetails = details => async dispatch => {
  try {
    // Set loading state to true
    dispatch({ type: SET_IS_LOADING_TRUE });

    // API Header configurations
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    };

    // Stringify JSON Body
    const body = JSON.stringify({
      company: {
        name: details.companyName,
        contactNo: details.mobileNumber,
        address: details.address || null,
        email: details.email || null,
      },
      user: {
        name: details.rootUserName || "default",
        username: details.rootUserUsername || "default",
        phone: details.rootUserMobileNumber || details.mobileNumber,
        email: details.rootUserEmail || details.email || null,
        password: details.password,
      },
    });

    // Send the POST request
    const response = await Axios.post(
      `/api/dashboard/onboarding/business-register`,
      body,
      config
    );

    if (response.status === 201) {
      // Dispatch success action with response data
      dispatch({
        type: SUBMIT_SUCCESSFUL,
        payload: response.data,
      });

      dispatch(
        showSnackbar({
          type: "success",
          msg: "Your company has been successfully created!",
        })
      );

      // Clear all details after successful submission
      dispatch({ type: CLEAR_ALL_DETAILS });
    } else {
      // Handle unexpected status codes
      dispatch({
        type: SUBMIT_FAILED,
        payload: response.data || "Submission failed with an unexpected status",
      });

      dispatch(
        showSnackbar({
          type: "error",
          msg: "Submission failed with an unexpected status",
        })
      );

      throw new Error(`Unexpected response status: ${response.status}`);
    }
  } catch (err) {
    // Dispatch failed action with error message
    dispatch({
      type: SUBMIT_FAILED,
      payload: err.response?.data?.message || err.message,
    });

    dispatch(
      showSnackbar({
        type: "error",
        msg: "Submission failed!",
      })
    );
  } finally {
    // Set loading state to false
    dispatch({ type: SET_IS_LOADING_FALSE });
  }
};

export const validateBusinessName =
  (businessName, contactNo) => async dispatch => {
    try {
      // Set loading state to true
      dispatch({ type: SET_IS_LOADING_TRUE });

      // API Header configurations
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      };

      // Send the GET request
      const response = await Axios.get(
        `/api/dashboard/onboarding/business-name-verification?businessName=${businessName}&contactNo=${contactNo}`,
        config
      );

      if (response.status === 200) {
        if (response.data.data == false) {
          dispatch(
            showSnackbar({
              type: "error",
              msg: `Business "${businessName}" is already registered in our system with ${contactNo}. If you need to reset or update this information, please contact our support team.`,
            })
          );
          return false;
        } else {
          return true;
        }
      } else {
        dispatch(
          showSnackbar({
            type: "error",
            msg: "Something went wrong! Please try again",
          })
        );

        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (err) {
      // Dispatch failed action with error message
      dispatch(
        showSnackbar({
          type: "error",
          msg: "Something went wrong! Please try again",
        })
      );
    } finally {
      // Set loading state to false
      dispatch({ type: SET_IS_LOADING_FALSE });
    }
  };
